import {
  Box,
  Flex,
  VStack,
  SimpleGrid,
  useToast,
  useBreakpointValue,
} from "@chakra-ui/react";
import { Link, useHistory } from 'react-router-dom';
import { SubmitHandler, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation } from "react-query";

import { ButtonDefault } from "../../../components/ButtonDefault";
import { Input } from "../../../components/Form/Input";
import { Header } from "../../../components/Header";
import { Sidebar } from "../../../components/Sidebar";
import { api } from "../../../services/api";
import { queryClient } from "../../../services/queryClient";
import ContentBase from "../../../components/ContentBase";
import MainBase from "../../../components/MainBase";
import { ClassSubjectExist } from "../../../utils/validations/ClassSubjectExist";

type CreateClassSubjectFormData = {
  subject: string;
  quantity: number;
}

const createClassSubjectFormSchema = yup.object().shape({
  subject: yup.string()
    .required('Tema obrigatório')
    .test(
      'tema-exists',
      'Tema já cadastrado, favor informar outro.',
      async (value) => (
        await ClassSubjectExist(value)
      )
    ),
})

export function CreateClassSubject() {
  const history = useHistory();
  const toast = useToast()

  const createClassSubject = useMutation(async (classSubject: CreateClassSubjectFormData) => {
    const response = await api.post('class-subjects', classSubject)

    return response.data.classSubject;
  }, {
    onSuccess: () => {
      queryClient.resetQueries('class-subjects')
    }
  });

  const { register, handleSubmit, formState } = useForm({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: yupResolver(createClassSubjectFormSchema)
  })

  const { errors } = formState

  const handleCreateClassSubject: SubmitHandler<CreateClassSubjectFormData> = async (values) => {
    try {
      await createClassSubject.mutateAsync({
        ...values,
      });

      toast({
        title: "Tema incluído",
        description: "Cadastro realizado com sucesso.",
        status: "success",
        duration: 3000, // 3 seconds,
        isClosable: true,
        position: "top-right",
      })

      history.push('/class-subjects')
    } catch (err: any) {
      toast({
        title: "Opa...",
        description: "Ocorreu uma instabilidade. Por favor tente novamente mais tarde.",
        status: "error",
        duration: 3000, // 3 seconds,
        isClosable: true,
        position: "top-right",
      })
    }
  }

  const isWideVersion = useBreakpointValue({
    base: false,
    lg: true,
  })

  return (
    <Flex direction="column" h="100vh">
      { !isWideVersion && <Header /> }

      <ContentBase>
        <Sidebar />

        <MainBase mainTitle="Incluir tema para aulas">
          <Box
            as="form"
            h={["", "80vh"]}
            overflow="auto"
            paddingRight="1"
            css={{
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-track': {
                width: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: "var(--chakra-colors-gray-400)",
                borderRadius: '25px',
              },
            }}
            onSubmit={handleSubmit(handleCreateClassSubject)}
          >
            <VStack spacing="8" color="blue.900">
              <SimpleGrid minChildWidth="248px" spacing={["6", "8"]} w="100%">
                <Input
                    nameInput="subject"
                    label="Tema"
                    labelSize="lg"
                    error={errors.subject}
                    isRequired
                    autoFocus
                    {...register('subject')}
                  />

                  <Input
                    nameInput="quantity"
                    type="number"
                    label="Quantidade máxima por aluno"
                    labelSize="lg"
                    error={errors.quantity}
                    defaultValue={1}
                    isRequired
                    {...register('quantity')}
                  />
              </SimpleGrid>
            </VStack>

            <Flex mt="8">
              <Flex
                w="100%"
                spacing={["", "4"]}
                flexDir={["column", "row"]}
                justifyContent={["normal", "flex-end"]}
              >
                <Box w={["100%", "auto"]}>
                  <Link to="/class-subjects" >
                    <ButtonDefault text="Cancelar" w={["100%", "150px"]} info />
                  </Link>
                </Box>

                <ButtonDefault
                  type="submit"
                  text="Salvar"
                  w={["100%", "150px"]}
                  mt={["3", "0"]}
                  ml={["0", "4"]}
                  isLoading={formState.isSubmitting}
                />
              </Flex>
            </Flex>
          </Box>
        </MainBase>
      </ContentBase>
    </Flex>
  );
}
